import { Box, Typography } from "@mui/material";
import type { Editor } from "@tiptap/react";
import { useCallback, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PageHeader } from "src/components";
import { FramesBreadcrumb } from "src/components/Frames/FramesBreadcrumb";
import { FrameViewerTopBar } from "src/components/Frames/FrameViewerTopBar";
import "src/components/TiptapEditor/TiptapEditor.css";
import { ROUTES, ROUTE_SLUGS } from "src/config/navigation/routes";
import { useGlobalDeviceContext } from "src/hooks";
import { useGetDevices, useGetFrameTemplate } from "src/hooks/queries";
import { DynamicNodeTemplatePreviewProvider } from "src/tiptap/components/tiptap-node/dynamic-nodes/dynamic-node-template-preview-provider";
import {
  SimpleEditor,
  type SimpleEditorVersionsContext,
} from "src/tiptap/components/tiptap-templates/simple/simple-editor";
import { fillRouteWithSlugs } from "src/utils";

/**
 * Read-only preview of a document template ("frame"): the backend-serialized
 * Tiptap content rendered with dynamic nodes resolving from the org and the
 * selected device.
 */
export const FrameViewerPage = () => {
  const { orgId = "", templateId = "" } = useParams<{
    orgId: string;
    templateId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeGlobalDeviceId } = useGlobalDeviceContext();
  const [editor, setEditor] = useState<Editor | null>(null);
  const [showAllConditionals, setShowAllConditionals] = useState(false);
  const templateQuery = useGetFrameTemplate(orgId, templateId);
  const devicesQuery = useGetDevices({ orgId });
  const devices = useMemo(() => devicesQuery.data ?? [], [devicesQuery.data]);

  // Device precedence: explicit ?deviceId → the app-wide active device → the
  // most recently created device. Unknown/deleted ids fall through.
  const deviceId = useMemo(() => {
    const candidates = [searchParams.get("deviceId"), activeGlobalDeviceId];
    for (const candidate of candidates) {
      if (candidate && devices.some((device) => device.id === candidate)) {
        return candidate;
      }
    }

    const newestFirst = [...devices].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return newestFirst[0]?.id;
  }, [activeGlobalDeviceId, devices, searchParams]);

  const handleDeviceSelect = useCallback(
    (nextDeviceId: string) => {
      setSearchParams((currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);
        nextSearchParams.set("deviceId", nextDeviceId);

        return nextSearchParams;
      });
    },
    [setSearchParams]
  );

  const handleVersionsContextChange = useCallback(
    (context: SimpleEditorVersionsContext) => {
      setEditor(context.readyEditor);
    },
    []
  );

  const documentsRoute = fillRouteWithSlugs(ROUTES.DOCUMENTS, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
  });

  const template = templateQuery.data;
  const isTemplateEmpty =
    !templateQuery.isLoading &&
    template !== undefined &&
    !(template.content?.content ?? []).length;
  const scopeLabel = template
    ? template.organizationId
      ? "Customized"
      : "Global"
    : null;

  return (
    <Box
      data-testid="frame-viewer-page"
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        p: 0,
      }}
    >
      <PageHeader
        pageTitleComponent={
          <FramesBreadcrumb
            isLoading={templateQuery.isLoading && template === undefined}
            items={[
              { label: "Documents", to: documentsRoute },
              { label: "Frames", to: `${documentsRoute}?view=frames` },
              { label: template?.defaultTitle ?? "Frame" },
            ]}
          />
        }
        rightPanelToggleRequiresDevice={false}
      />
      <div
        className="tiptap-editor-page relative flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-white"
        data-show-all-conditionals={showAllConditionals}
      >
        <FrameViewerTopBar
          deviceId={deviceId}
          devices={devices}
          editor={editor}
          onDeviceSelect={handleDeviceSelect}
          onShowAllConditionalsChange={setShowAllConditionals}
          scopeLabel={scopeLabel}
          showAllConditionals={showAllConditionals}
        />
        {templateQuery.isError ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              This frame could not be loaded.
            </Typography>
          </Box>
        ) : isTemplateEmpty ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              This frame has no content.
            </Typography>
          </Box>
        ) : (
          <DynamicNodeTemplatePreviewProvider
            deviceId={deviceId}
            onShowAllConditionalsChange={setShowAllConditionals}
            orgId={orgId}
            showAllConditionals={showAllConditionals}
          >
            <div className="min-h-0 flex-1 overflow-hidden">
              <SimpleEditor
                conditionalDecorationsInReadOnly
                deviceId={deviceId}
                documentContent={template?.content ?? null}
                documentId={templateId}
                isLoading={templateQuery.isLoading}
                isReadOnly
                loadingLabel="Loading frame…"
                onVersionsContextChange={handleVersionsContextChange}
                orgId={orgId}
              />
            </div>
          </DynamicNodeTemplatePreviewProvider>
        )}
      </div>
    </Box>
  );
};

export default FrameViewerPage;
